// src/components/Footer.js
// Footer with all articles referenced across all sections

import React, { useMemo, useState } from 'react';
import './Footer.css';

const Footer = ({ articles = [], date }) => {
  const [showAllSources, setShowAllSources] = useState(false);

  // Process articles with favicon URLs
  const processedArticles = useMemo(() => {
    return articles.map(article => ({
      ...article,
      faviconUrl: article.domain
        ? `https://www.google.com/s2/favicons?domain=${article.domain}&sz=32`
        : null
    }));
  }, [articles]);

  const shareSite = () => {
    const shareUrl = 'https://skimz.ai';
    const tweetText = "Check out today's Skimz briefing";
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(tweetText)}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');

    if (window.mixpanel) {
      window.mixpanel.track('Footer Share', { platform: 'twitter' });
    }
  };

  const currentYear = new Date().getFullYear();

  const formatDataDate = (dateStr) => {
    if (!dateStr) return 'today';
    try {
      const dateObj = new Date(dateStr);
      return dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const maxVisible = 5;
  const totalSources = processedArticles.length;
  const hasOverflow = totalSources > maxVisible;
  const displayedSources = hasOverflow ? processedArticles.slice(0, maxVisible - 1) : processedArticles.slice(0, maxVisible);
  const overflowCount = totalSources - displayedSources.length;

  const handleToggle = (e) => {
    if (e.target.closest('a')) return;
    setShowAllSources(!showAllSources);
  };

  return (
    <footer className="footer">
      <div className="footer-content">
        {processedArticles.length > 0 && (
          <div className="sources-container">
            <h2 className="sources-heading">SOURCES REFERENCED</h2>

            <div className={`sources-footer-row ${showAllSources ? 'sources-expanded' : ''}`}>
              <div
                className="sources-collapsed-row"
                onClick={handleToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleToggle(e)}
              >
                <div className="sources-circles">
                  {displayedSources.map((article, index) => (
                    <span
                      key={article.url}
                      className="source-circle"
                      style={{ zIndex: maxVisible - index }}
                      title={article.publication}
                    >
                      {article.faviconUrl && (
                        <img
                          src={article.faviconUrl}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </span>
                  ))}
                  {hasOverflow && (
                    <span
                      className="source-circle source-overflow-count"
                      style={{ zIndex: 0 }}
                      title={`${overflowCount} more sources`}
                    >
                      +{overflowCount}
                    </span>
                  )}
                </div>
                <span className="sources-count">
                  {totalSources} {totalSources === 1 ? 'source' : 'sources'}
                </span>
              </div>

              {showAllSources && (
                <div className="sources-expanded-list">
                  {processedArticles.map((article) => (
                    <a
                      key={article.url}
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="source-expanded-item"
                    >
                      {article.faviconUrl && (
                        <img
                          src={article.faviconUrl}
                          alt=""
                          className="source-expanded-favicon"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <span className="source-expanded-text">
                        <span className="source-expanded-publication">{article.publication}</span>
                        {article.title && (
                          <>
                            <span className="source-expanded-separator"> — </span>
                            <span className="source-expanded-title">{article.title}</span>
                          </>
                        )}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="footer-bottom">
          <div className="copyright">
            <p>&copy; {currentYear} Skimz. All rights reserved.</p>
          </div>

          <div className="update-info">
            <p>Briefing for {formatDataDate(date)}</p>

            <div className="share-container">
              <button className="share-button" onClick={shareSite} aria-label="Share on X" title="Share on X">
                Share on
                <svg className="share-icon x-logo" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
